import React, { useEffect, useState } from 'react';
import { useVibrancy } from '../VibrancyDashboardContext';
import { Avatar, HeaderIcon, SectionDivider, cardHeaderStyle, cardStyle } from './shared';
import { EngagedUser, VibrancyEngagementService } from '../vibrancyEngagementService';
import { Period } from '../vibrancyDashboardService';

// Display labels for the page-level period filter (mirrors SelectorBar's TIMEFRAMES)
const PERIOD_LABELS: Record<string, string> = {
    monthly:    'Monthly',
    quarterly:  'Quarterly',
    halfyearly: 'Half-Yearly',
    annual:     'Annual',
};

const AVATAR_PALETTE = [
    { bg: '#fef3c7', color: '#92400e' },
    { bg: '#d1fae5', color: '#065f46' },
    { bg: '#cffafe', color: '#0891b2' },
    { bg: '#ebf2ff', color: '#1b52c4' },
    { bg: '#ede9fe', color: '#7c3aed' },
];

// studentId may arrive as 32-char hex (no hyphens); the profile route expects a
// hyphenated UUID (8-4-4-4-12). Format it; pass through anything already hyphenated.
const toUuid = (id: string): string => {
    const h = String(id || '').replace(/-/g, '');
    return h.length === 32
        ? `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
        : String(id || '');
};

const SkeletonRow: React.FC<{ i: number }> = ({ i }) => (
    <div
        className={`d-flex align-items-center gap-2 py-2 placeholder-glow ${i < 4 ? 'border-bottom' : ''}`}
        style={{ borderColor: '#f1f4fb' }}
    >
        <span className="placeholder rounded" style={{ width: 18, height: 14, display: 'block', minWidth: 22 }} />
        <span className="placeholder rounded-circle flex-shrink-0" style={{ width: 30, height: 30, display: 'block' }} />
        <div className="flex-grow-1">
            <span className="placeholder col-7 d-block mb-1" style={{ height: 12 }} />
            <span className="placeholder col-4 d-block" style={{ height: 10 }} />
        </div>
    </div>
);

const AlumniRow: React.FC<{ u: EngagedUser; i: number; last: boolean }> = ({ u, i, last }) => {
    const av = AVATAR_PALETTE[i % AVATAR_PALETTE.length];
    const initials = (u.name || 'A').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
    return (
        <div
            className={`d-flex align-items-center gap-2 py-2 ${!last ? 'border-bottom' : ''}`}
            style={{ borderColor: '#f1f4fb' }}
        >
            <div className="text-center fw-bold flex-shrink-0"
                style={{ fontSize: 13, color: u.rank <= 3 ? '#f97316' : '#8fa0c0', minWidth: 22 }}>
                {u.rank}
            </div>
            <Avatar initials={initials} bg={av.bg} color={av.color} />
            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <a
                    href={`${process.env.REACT_APP_APP_BASE_URL}/alumni/${toUuid(u.studentId)}/profile`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fw-medium font-14 text-truncate d-block text-dark"
                    style={{ cursor: 'pointer' }}
                    title={`View ${u.name}'s profile`}
                >
                    {u.name}
                </a>
                <div className="font-11 text-secondary">
                    {u.passOutYear ? `Batch ${u.passOutYear}` : 'Alumni'}
                </div>
            </div>
            {/* No. of actions (email delivered, login, profile view, etc.) */}
            <div className="fw-bold font-14 text-end flex-shrink-0"
                style={{ minWidth: 40, color: '#1b52c4' }}>
                {(u.activityCount ?? 0).toLocaleString()}
            </div>
        </div>
    );
};

/** VD-03 — Top 5 Engaged Users: Overall (all-time) + Period-based (local filter). */
const VibrancyPoints: React.FC<{ period?: Period }> = ({ period }) => {
    const { selectedInstituteId, instituteLoaded, timeframe: globalTimeframe } = useVibrancy();
    const timeframe = period ?? globalTimeframe;   // section period overrides the global one

    const [overallList, setOverallList] = useState<EngagedUser[]>([]);
    const [periodList, setPeriodList]   = useState<EngagedUser[]>([]);
    const [loadingOverall, setLoadingOverall] = useState(false);
    const [loadingPeriod,  setLoadingPeriod]  = useState(false);

    // LEFT — all-time, re-fetches only when institute changes
    useEffect(() => {
        if (!selectedInstituteId) { setOverallList([]); return; }
        setLoadingOverall(true);
        VibrancyEngagementService.getTopOverall(selectedInstituteId)
            .then(r  => setOverallList(r.data?.data || []))
            .catch(() => setOverallList([]))
            .finally(() => setLoadingOverall(false));
    }, [selectedInstituteId]);

    // RIGHT — period-based, driven by the page-level (top) period filter.
    // Re-fetches when institute or the global timeframe changes.
    useEffect(() => {
        if (!selectedInstituteId) { setPeriodList([]); return; }
        setLoadingPeriod(true);
        VibrancyEngagementService.getTopByPeriod(selectedInstituteId, timeframe)
            .then(r  => setPeriodList(r.data?.data || []))
            .catch(() => setPeriodList([]))
            .finally(() => setLoadingPeriod(false));
    }, [selectedInstituteId, timeframe]);

    if (!instituteLoaded) return null;

    const periodLabel = PERIOD_LABELS[timeframe] ?? timeframe;

    return (
        <>
            {/* <SectionDivider
                icon="⚡"
                title="Top 5 Engaged Users"
                note="ranked by activity count · institute-wise"
            /> */}
            
            <div className="row g-3 mb-3">
                {/* LEFT — Overall (all-time, not affected by page period) */}
                <div className="col-md-6">
                    <div className="card h-100" style={cardStyle}>
                        <div className="card-header bg-white d-flex justify-content-between align-items-center" style={cardHeaderStyle}>
                            <div className="fw-medium d-flex align-items-center gap-2 font-14">
                                <HeaderIcon icon="🏆" bg="#ebf2ff" color="#1b52c4" />
                                Top Engaged Users
                            </div>
                            <span className="text-uppercase fw-bold text-secondary" style={{ fontSize: 9.5, letterSpacing: .5 }}>Actions</span>
                        </div>
                        <div className="card-body py-2">
                            {loadingOverall
                                ? [0,1,2,3,4].map(i => <SkeletonRow key={i} i={i} />)
                                : overallList.length === 0
                                    ? <div className="text-center py-4 text-muted small">No activity data available</div>
                                    : overallList.map((u, i) => (
                                        <AlumniRow
                                            key={u.studentId}
                                            u={u} i={i}
                                            last={i === overallList.length - 1}
                                        />
                                    ))
                            }
                        </div>
                    </div>
                </div>

                {/* RIGHT — Period-based, driven by the top page-level period filter */}
                <div className="col-md-6">
                    <div className="card h-100" style={cardStyle}>
                        <div className="card-header bg-white d-flex justify-content-between align-items-center flex-wrap gap-2" style={cardHeaderStyle}>
                            <div className="fw-medium d-flex align-items-center gap-2 font-14">
                                <HeaderIcon icon="📈" bg="#d1fae5" color="#0f9d58" />
                                Top Engaged Users
                                <span className="badge bg-success bg-opacity-10 border border-success border-opacity-25 font-11 fw-medium text-success-emphasis">
                                    {periodLabel}
                                </span>
                            </div>
                            <span className="text-uppercase fw-bold text-secondary" style={{ fontSize: 9.5, letterSpacing: .5 }}>Actions</span>
                        </div>
                        <div className="card-body py-2">
                            {loadingPeriod
                                ? [0,1,2,3,4].map(i => <SkeletonRow key={i} i={i} />)
                                : periodList.length === 0
                                    ? <div className="text-center py-4 text-muted small">No activity data for this period</div>
                                    : periodList.map((u, i) => (
                                        <AlumniRow
                                            key={u.studentId}
                                            u={u} i={i}
                                            last={i === periodList.length - 1}
                                        />
                                    ))
                            }
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default VibrancyPoints;
